import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Row, Space, Statistic, Tag, Typography, message } from 'antd';
import { CopyOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import AppLayout from '../../components/layout/AppLayout';
import {
  lienParrainage, lienWhatsApp, useApporteursApi, type TApporteur, type TDetailMois,
} from '../../api/paiement/apporteurs';
import { libelleAvantage } from './avantage';
import ListeEnfants from './ListeEnfants';

// Espace de l'animateur : son code, son lien de parrainage (à copier ou à
// partager sur WhatsApp, l'usage principal).
//
// L'animateur affiché est TOUJOURS celui de la session : le serveur ne lit
// aucun identifiant envoyé par l'écran.

const EspaceApporteur: React.FC = () => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const [apporteur, setApporteur] = useState<TApporteur | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(false);
    try {
      setApporteur(await api.moi());
    } catch {
      setErreur(true);
    } finally {
      setLoading(false);
    }
    // api change d'identité à chaque rendu : le laisser en dépendance
    // relancerait le chargement en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const code = apporteur?.code;
  const lien = code?.actif ? lienParrainage(code.code, apporteur?.lien_parrainage) : '';

  const copier = async (texte: string) => {
    try {
      await navigator.clipboard.writeText(texte);
      message.success(t('apporteur.copie', 'Copié'));
    } catch {
      message.error(t('apporteur.copieErreur', 'Copie impossible : sélectionnez le texte pour le copier'));
    }
  };

  const messageWhatsApp = t(
    'apporteur.messageWhatsApp',
    "Inscrivez votre enfant sur Tilmicode, la plateforme où les enfants apprennent à coder : {{lien}}",
    { lien },
  );

  return (
    <AppLayout title={t('apporteur.titre', 'Mon espace animateur')} loading={loading}>
      {erreur && (
        <Alert type="error" showIcon message={t('apporteur.chargementErreur', "Impossible de charger votre espace")} />
      )}
      {apporteur && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {!apporteur.actif && (
            <Alert
              type="warning"
              showIcon
              message={t('apporteur.inactif', "Votre compte animateur est désactivé")}
              description={t(
                'apporteur.inactifDetail',
                "Votre code n'enregistre plus de nouveaux enfants. Les enfants déjà apportés restent comptés.",
              )}
            />
          )}
          <Card title={t('apporteur.monCode', 'Mon code')}>
            {code ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                  <Typography.Title level={3} style={{ margin: 0 }} copyable={{ text: code.code }}>
                    {code.code}
                  </Typography.Title>
                  {code.actif
                    ? <Tag color="green">{t('apporteur.codeActif', 'Actif')}</Tag>
                    : <Tag>{t('apporteur.codeInactif', 'Inactif')}</Tag>}
                </Space>
                <Typography.Text type="secondary">
                  {libelleAvantage(t, code.avantage)}
                </Typography.Text>
              </Space>
            ) : (
              <Typography.Text type="secondary">{t('apporteur.sansCode', "Aucun code ne vous est attribué")}</Typography.Text>
            )}
          </Card>
          <MoisApporteur />
          {lien && (
            <Card title={t('apporteur.monLien', 'Mon lien de parrainage')}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Typography.Paragraph style={{ margin: 0, wordBreak: 'break-all' }}>{lien}</Typography.Paragraph>
                <Space wrap>
                  <Button icon={<CopyOutlined />} onClick={() => copier(lien)}>
                    {t('apporteur.copierLien', 'Copier le lien')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<WhatsAppOutlined />}
                    href={lienWhatsApp(messageWhatsApp)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('apporteur.partagerWhatsApp', 'Partager sur WhatsApp')}
                  </Button>
                </Space>
              </Space>
            </Card>
          )}
        </Space>
      )}
    </AppLayout>
  );
};

/**
 * Le mois choisi (le mois en cours par défaut) : compteurs premium et
 * freemium, et la liste qui les justifie. Calculé en direct par le serveur :
 * un mois passé se lit à son dernier instant, le mois en cours maintenant.
 */
const MoisApporteur: React.FC = () => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [mois, setMois] = useState<Dayjs>(dayjs());
  const [detail, setDetail] = useState<TDetailMois | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let vivant = true;
    setLoading(true);
    apiRef.current
      .monMois(mois.format('YYYY-MM'))
      .then((d) => vivant && setDetail(d))
      .catch(() => vivant && message.error(t('apporteur.moisErreur', 'Impossible de charger ce mois')))
      .finally(() => vivant && setLoading(false));
    return () => {
      vivant = false;
    };
  }, [mois, t]);

  return (
    <Card
      title={t('apporteur.mesEnfants', 'Enfants apportés')}
      extra={
        <DatePicker
          picker="month"
          allowClear={false}
          value={mois}
          onChange={(v) => v && setMois(v)}
          disabledDate={(d) => d.isAfter(dayjs(), 'month')}
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={16}>
          <Col xs={12}>
            <Statistic title={t('apporteur.premium', 'Premium')} value={detail?.premium ?? 0} loading={loading} />
          </Col>
          <Col xs={12}>
            <Statistic title={t('apporteur.freemium', 'Freemium')} value={detail?.freemium ?? 0} loading={loading} />
          </Col>
        </Row>
        {detail && (
          <Typography.Text type="secondary">
            {detail.en_cours
              ? t('apporteur.moisEnCours', 'Mois en cours : statut de chaque enfant aujourd’hui.')
              : t('apporteur.moisPasse', 'Statut de chaque enfant au dernier jour du mois.')}
          </Typography.Text>
        )}
        <ListeEnfants enfants={detail?.enfants ?? []} loading={loading} />
      </Space>
    </Card>
  );
};

export default EspaceApporteur;
